import {useEditStep} from '../queries/useEditStep'
import {useEditExperience} from '../queries/useEditExperience'
import {errorHandler} from '@/utilities/helpers'
import {ShellStepKeyName} from '../types'
import {useState} from 'react'
import {useExperienceShellFormContext} from './useExperienceShellFormContext'
import {httpEditExperienceOptions} from '../http/experienceShell.http'
import {makeShellFormDefaultValues} from '../utils'
import {useExpItinerariesPosition} from './useExpItinerariesPosition'
import {useGetExperience} from '../queries/useGetExperience'

export const useChangeStep = ({
    experienceId,
    currentStep,
    isUpgrade
}: {
    experienceId: number
    currentStep: ShellStepKeyName
    isUpgrade: boolean
}) => {
    const [isBackPending, setIsBackPending] = useState(false)
    const {getValues, trigger, reset} = useExperienceShellFormContext(isUpgrade)
    const {data: experienceData} = useGetExperience(experienceId)

    const editStepMutation = useEditStep({
        experienceId,
        options: {
            onError: errorHandler,
            onSuccess: data => {
                //@ts-expect-error "I need to reset the form with the new values from the backend"
                reset(makeShellFormDefaultValues(data))
            }
        }
    })
    const editExperienceMutation = useEditExperience({
        experienceId,
        options: {onError: errorHandler}
    })
    const itineraryPositionMutation = useExpItinerariesPosition({
        urlParams: {experienceId},
        options: {onError: errorHandler}
    })
    const isBackLoading =
        isBackPending &&
        (editStepMutation.isPending || editExperienceMutation.isPending || itineraryPositionMutation.isPending)
    const isContinueLoading =
        !isBackPending &&
        (editStepMutation.isPending || editExperienceMutation.isPending || itineraryPositionMutation.isPending)

    const stepToEditStepKeyName = (step: ShellStepKeyName) => {
        if (step == ShellStepKeyName.enum.pricing_discounts) {
            return 'pricing-discounts'
        }
        return step
    }

    const getPayload = (): httpEditExperienceOptions['payload'] => {
        if (currentStep == ShellStepKeyName.enum.photos) {
            return {}
        }
        if (currentStep == ShellStepKeyName.enum.background) {
            const values = getValues(currentStep)
            return {
                ...values,
                online_profile_links: values.online_profile_links?.filter(link => !!link.link).map(link => link.link)
            }
        }
        if (currentStep == ShellStepKeyName.enum.legal) {
            const values = getValues(currentStep)
            const {categoryKeyName, ...rest} = values
            return rest
        }
        return getValues(currentStep)
    }

    const updateItinerariesPositions = async () => {
        if (experienceData && experienceData.itineraries) {
            const itineraries = experienceData.itineraries || []
            if (itineraries.length > 0) {
                const positionsPayload = itineraries.map((itinerary: {id: number}, index: number) => ({
                    id: itinerary.id,
                    position: index + 1
                }))
                await itineraryPositionMutation.mutateAsync(positionsPayload)
            }
        }
    }

    const handleChangeStep = async ({isBack, stepToGoTo}: {isBack?: boolean; stepToGoTo: ShellStepKeyName}) => {
        if (isUpgrade) {
            const isValid = currentStep != ShellStepKeyName.enum.photos ? await trigger(currentStep) : true
            if (!isValid) return
        }
        await updateItinerariesPositions()
        setIsBackPending(!!isBack)

        await editExperienceMutation.mutateAsync(getPayload())
        await editStepMutation.mutateAsync(stepToEditStepKeyName(stepToGoTo))
        setIsBackPending(false)
    }

    return {
        editStepMutation,
        editExperienceMutation,
        itineraryPositionMutation,
        isBackLoading,
        isContinueLoading,
        handleChangeStep,
        updateItinerariesPositions
    }
}
